import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import styles from './../../../style';
import moment from 'moment';
import { getPastSoapNoteById, getPastSoapNoteDetails } from 'services/soap/action';
import {
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  Modal,
  Avatar,
  Chip
} from '@material-ui/core';
import {
  LastPage,
  CloseOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
  NavigateNext,
  NavigateBefore
} from '@material-ui/icons';
import { connect } from 'react-redux';
import { EncounterSummary } from './';
import MessageBox from 'util/messageBox';
import { Skeleton } from '@material-ui/lab';

// Moved outside component to avoid re-creation on every render
const TableRowsLoader = ({ rowsNum = 10 }) => (
  <>
    {[...Array(rowsNum)].map((_, index) => (
      <TableRow key={index}>
        <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
        <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
        <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
      </TableRow>
    ))}
  </>
);

const SoapPast = ({ classes, patientId, getPastSoapNoteById, soapNotes, userID, ehrID, ...props }) => {
  const [openSoap, setOpenSoap] = useState({ open: false, soap: null });
  const [isHistory, setIsHistory] = useState(false);
  const [page, setPage] = useState(0);
  const [pastSoapNote, setPastSoapNote] = useState(null);
  const [pastSoapNoteLoading, setPastSoapNoteLoading] = useState(true);
  const [succmsg, setSuccMsg] = useState({ succ: false, succMsg: '' });
  const [errmsg, setErrMsg] = useState({ err: false, errMsg: '' });

  useEffect(() => {
    if (!patientId) return;
    setPastSoapNoteLoading(soapNotes?.content ? false : true);
    setPastSoapNote(soapNotes);
  }, [soapNotes, patientId]);

  const toggleHistory = useCallback(() => setIsHistory(prev => !prev), []);
  
  const handleOpenSoap = useCallback(item => (setOpenSoap({ open: true, soap: item })
  ), []);

  const handleCloseSoap = useCallback(() => setOpenSoap({ open: false, soap: null }), []);

  const handleCopyPast = useCallback((soap, pastSoapNoteDetails) => {
    props.handleCopyPast?.(soap, pastSoapNoteDetails);
    setOpenSoap({ open: false, soap: null });
  }, [props]);

  const handleEditPast = useCallback((soap, pastSoapNoteDetails) => {
    props.handleEditPast?.(soap, pastSoapNoteDetails);
    setOpenSoap({ open: false, soap: null });
  }, [props]);

  const showSuccessMsg = useCallback(msg => setSuccMsg({ succ: true, succMsg: msg }), []);

  // Fixed: stale closure bug — compute next page value first, then set state and call API
  const handleChange = useCallback(async nav => {
    setPage(prevPage => {
      let nextPage;
      if (nav === 'next') nextPage = prevPage + 1;
      else if (nav === 'first') nextPage = 0;
      else nextPage = Math.max(prevPage - 1, 0);

      getPastSoapNoteById(patientId, nextPage)
        .then(res => setPastSoapNote(res))
        .catch(() => setErrMsg({ err: true, errMsg: 'Failed to load past SOAP notes.' }));

      return nextPage;
    });
  }, [patientId, getPastSoapNoteById]);

  const renderSuccess = useMemo(() =>
    succmsg.succ ? (
      <MessageBox
        message={succmsg.succMsg}
        onClose={() => setSuccMsg({ succ: false })}
        open={succmsg.succ}
        variant="success"
      />
    ) : null,
    [succmsg]);

  const renderError = useMemo(() =>
    errmsg.err ? (
      <MessageBox
        message={errmsg.errMsg}
        onClose={() => setErrMsg({ err: false })}
        open={errmsg.err}
        variant="error"
      />
    ) : null,
    [errmsg]);

  const content = pastSoapNote?.content ?? [];
  const totalPages = pastSoapNote?.totalPages ?? 0;
  const isLast = pastSoapNote?.last === true;

  const getStatusLabel = item => {
    if (item.signed) return `Signed by: ${item.provider?.firstname || ''} ${item.provider?.lastname || ''}`.trim();
    if (item.routed) return `Routed to: ${item.provider?.firstname || ''} ${item.provider?.lastname || ''}`.trim();
    return `Created by: ${item.provider?.firstname || ''} ${item.provider?.lastname || ''}`.trim();
  };

  return (
    <Box className={classes.soapPastWrap}>
      {/* Header */}
      <Box className={classes.soapPastHeader}>
        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
          Past SOAP Notes
        </Typography>
        {!props.isPast && (
          <IconButton onClick={() => props.setPast?.()} size="small">
            <LastPage />
          </IconButton>
        )}
      </Box>

      {/* Table */}
      <Box>
        <PerfectScrollbar style={{ height: '90vh' }}>
          <Table className={clsx(classes.plainTable, classes.blueBg)} style={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 100, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', }}>Date</TableCell>
                <TableCell style={{ width: '55%', fontSize: 11, fontWeight: 500, textTransform: 'uppercase' }}>Subjective</TableCell>
                <TableCell style={{ width: 160, fontSize: 11, fontWeight: 500, textTransform: 'uppercase' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pastSoapNoteLoading && <TableRowsLoader />}
              {!pastSoapNoteLoading && totalPages === 0 && (
                <TableRow>
                  <TableCell colSpan={3} style={{ color: 'rgba(0,0,0,0.4)', fontStyle: 'italic' }}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
              {Array.isArray(content) && content.map(item => (
                <TableRow
                  key={item.id || item.visitdate}
                  onClick={() => handleOpenSoap(item)}
                  hover
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell >
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      {!item.signed && (
                        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#E24B4A', display: 'inline-block', flexShrink: 0 }} />
                      )}
                      <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {item.visitdate ? moment(item.visitdate).format('MM/DD/YYYY') : '—'}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell
                    style={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      fontSize: 13
                    }}
                  >
                    {item.soapnote?.subjective?.substring(0, 100) || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(item)}
                      size="small"
                      style={{
                        fontSize: 12,
                        height: 20,
                        backgroundColor: item.signed ? '#eaf3de05' : item.routed ? '#E6F1FB' : '#F1EFE8',
                        color: item.signed ? '#2e6402' : item.routed ? '#185FA5' : '#5F5E5A',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PerfectScrollbar>
      </Box>

      {/* Pagination — only shown when there's more than one page */}
      {totalPages > 1 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          style={{ gap: 8, padding: '8px 16px', borderTop: '0.5px solid rgba(0,0,0,0.1)' }}
        >
          <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.5)' }}>
            Page {page + 1} of {totalPages}
          </Typography>
          <IconButton size="small" disabled={page === 0} onClick={() => handleChange('prev')}>
            <NavigateBefore fontSize="small" />
          </IconButton>
          <IconButton size="small" disabled={isLast} onClick={() => handleChange('next')}>
            <NavigateNext fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Modal */}
      <Modal
        aria-describedby="simple-modal-description"
        aria-labelledby="simple-modal-title"
        disableBackdropClick
        onClose={handleCloseSoap}
        open={Boolean(openSoap.open)}
      >
        <Box className={classes.modalWrap} style={{ width: '95%', maxWidth: 1200 }}>
          <Box className={classes.modalHead}>
            <Typography style={{ display: 'flex', alignItems: 'center' }} variant="h2">
              {openSoap.soap?.visitdate
                ? `Encounter Date: ${moment(openSoap.soap.visitdate).format('MM/DD/YYYY')}`
                : ''}
            </Typography>
            <span>
              <Button
                color="primary"
                onClick={toggleHistory}
                size="small"
                startIcon={isHistory ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
                style={{ textTransform: 'capitalize' }}
                variant="outlined"
              >
                {isHistory ? 'Show' : 'Hide'}&nbsp;Medical History
              </Button>
              <IconButton aria-label="close" onClick={handleCloseSoap}>
                <CloseOutlined />
              </IconButton>
            </span>
          </Box>
          <EncounterSummary
            {...props}
            page={page}
            isHistory={isHistory}
            showSuccessMsg={showSuccessMsg}
            handleCopyPast={handleCopyPast}
            handleEditPast={handleEditPast}
            soap={openSoap.soap}
            setHistory={toggleHistory}
            onCancel={handleCloseSoap}
          />
        </Box>
      </Modal>

      {renderSuccess}
      {renderError}
    </Box>
  );
};

SoapPast.propTypes = {
  className: PropTypes.string,
};

const mapDispatchToProps = { getPastSoapNoteById, getPastSoapNoteDetails };

const mapStateToProps = state => ({
  ehrID: state.profile?.ehrID,
  userID: state.profile?.userID,
  patientId: state.soap?.patientInfo?.id,
  soapNotes: state.soap?.pastSoapNote,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SoapPast))
);